import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from './ui/card';
import { Target, User, Heart, Zap, Globe, Award } from 'lucide-react';

const About: React.FC = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 60 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const stagger = {
    animate: {
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  return (
    <section id="about" className="py-20 bg-slate-50">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
          variants={stagger}
          className="max-w-6xl mx-auto"
        >
          {/* Header */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About <span className="text-blue-600">Mario</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              A passionate full-stack developer from the beautiful island of Palawan, dedicated to creating 
              exceptional digital experiences with cutting-edge technologies.
            </p>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-12 items-start">
            {/* Profile Card */}
            <motion.div variants={fadeInUp}>
              <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-2xl rounded-3xl overflow-hidden">
                <CardContent className="p-0">
                  <div className="bg-blue-600 p-8 text-white">
                    <div className="flex items-center space-x-4 mb-6">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                        <User className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">Mario W. Roco</h3>
                        <p className="text-indigo-100">Full-Stack Developer</p>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center space-x-2">
                        <Globe className="h-4 w-4" />
                        <span>Puerto Princesa City, Palawan</span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Award className="h-4 w-4" />
                        <span>5+ Years of Experience</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-8">
                    <h4 className="text-xl font-semibold text-gray-900 mb-4 flex items-center">
                      <Target className="h-5 w-5 mr-2 text-indigo-500" />
                      Career Objective
                    </h4>
                    <p className="text-gray-700 leading-relaxed">
                      Detail-oriented and highly organized professional with a strong ability to meet tight deadlines 
                      while maintaining quality. A motivated team player, eager to contribute to company success 
                      with enthusiasm and dedication.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Key Highlights */}
            <motion.div variants={fadeInUp} className="space-y-6">
              <h4 className="text-2xl font-bold text-gray-900 mb-8">
                Why Choose Mario?
              </h4>
              
              {[
                {
                  icon: Zap,
                  title: "Performance Focused",
                  description: "Optimizing applications for speed, efficiency, and scalability across all platforms.",
                  color: "bg-yellow-500"
                },
                {
                  icon: Heart,
                  title: "Quality Driven",
                  description: "Maintaining high coding standards with thorough testing and clean, maintainable code.",
                  color: "bg-pink-500"
                },
                {
                  icon: Globe,
                  title: "Remote Ready",
                  description: "Experienced in remote collaboration with teams across Australia, Czech Republic, and more.",
                  color: "bg-blue-500"
                },
                {
                  icon: Target,
                  title: "Deadline Focused",
                  description: "Proven track record of delivering projects on time without compromising quality.",
                  color: "bg-green-500"
                }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="flex items-start space-x-4 group"
                >
                  <div className={`w-12 h-12 ${item.color} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 rounded-xl`}>
                    <item.icon className="h-6 w-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <h5 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h5>
                    <p className="text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            variants={fadeInUp}
            className="text-center mt-16 p-8 bg-blue-50 rounded-3xl border border-blue-200"
          >
            <h4 className="text-2xl font-bold text-gray-900 mb-4">
              Ready to Build Something Amazing Together?
            </h4>
            <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
              Let's discuss your next project and see how we can bring your vision to life 
              with modern technology and creative solutions.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                const element = document.getElementById('contact');
                if (element) element.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Start a Conversation
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default About;